/**
 * Pan/zoom for one diagram. Dependency-free: a CSS transform on a wrapper,
 * driven by wheel (zoom), drag (pan), and three small controls.
 *
 * Diagrams are the product's answer surface now, and a real one outgrows its
 * pane the moment a flow has more than a handful of hops. The rules:
 *  - FIT-TO-VIEW is the resting state: a freshly drawn diagram lands centred
 *    and whole, at whatever size the pane happens to be.
 *  - The wheel zooms toward the pointer (the page behind never scrolls: the
 *    pane owns the gesture), 25% to 400%, by the distance the wheel travels
 *    rather than by the event (see WHEEL_PX_PER_DOUBLING).
 *  - Drag pans, and ONLY pans: the surface refuses text selection, so a
 *    drag across the SVG's labels never turns into a highlight.
 *    The Fit control refits; a double-click does nothing (2026-09-06; owner:
 *    "I don't like how double clicking on the map fits"), which is what a
 *    reader clicking twice on a part expects.
 *  - A gesture that STARTS on a clickable part of the diagram (round 4) is a
 *    click on that part, not a pan of the canvas. Otherwise the surface would
 *    take pointer capture off the element, and the click that opens the
 *    part's panel might never be delivered to it.
 *
 * Round 5 (owner, on a real answer): "the diagram is not centred and is cut
 * off". It was, and the reason was that nothing ever fitted anything. The
 * drawing was laid out by CSS — an SVG stretched to `width: 100%` inside a
 * centring flex box — and mermaid's own inline `max-width` won that argument,
 * so the SVG sat at its natural width against the LEFT edge of a full-width
 * box while its height, derived from that width through the viewBox ratio,
 * ran off the bottom of the pane. Layout cannot fit a drawing to a box in two
 * dimensions; a transform can. So the drawing is now placed at its natural
 * size and the transform does the framing:
 *
 *   scale = min(frame / content) in both axes, capped, then centred.
 *
 * It is recomputed whenever the frame or the drawing changes size — the parts
 * panel docking open, the transcript column being dragged, a depth switch
 * swapping the picture — but ONLY while the reader has not framed it
 * themselves. Once you pan or zoom, that is your view until you ask for the
 * fit back.
 *
 * Round 6 adds a second drag to the same surface, and the whole question is
 * how it avoids fighting the first. The rule: A PLAIN DRAG IS ALWAYS A PAN.
 * Holding shift (or cmd/ctrl) turns the drag into a rubber band that picks
 * every part it crosses. Modifier, not a mode toggle, because:
 *  - shift already means "add to what I have" for a click on a part, so there
 *    is ONE thing to learn for the whole feature rather than two;
 *  - it is modeless, so the gesture readers use constantly (dragging a picture
 *    that does not fit) is never taken away from them, and there is no state
 *    they can leave switched on and be surprised by a minute later.
 * A band only ever STARTS on empty canvas: a shift-press on a part is that
 * part being added to the selection, which is the same rule round 4 wrote for
 * plain clicks (a gesture that starts on a part belongs to the part).
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { PART_ATTR, type ScreenRect } from './partHotspots';

const MIN_SCALE = 0.25;
const MAX_SCALE = 4;
const ZOOM_STEP = 1.25;
/**
 * Wheel travel, in pixels, that doubles the picture (or halves it). The old
 * rule was one ZOOM_STEP per wheel event, and a laptop trackpad reports one
 * swipe as dozens of events a few pixels each, so a flick ran the scale from
 * the floor to the ceiling in a moment (owner, 2026-09-16: "scrolling is too
 * sensitive, esp on laptop trackpad ... it zooms in/out too fast"). Now the
 * zoom follows the distance travelled, however the device chops it up: a
 * mouse notch (about 100px in Chrome) is a gentle step, a swipe is a doubling
 * or so, and no single event may exceed one ZOOM_STEP. The figure is
 * d3-zoom's calibration, which most map-like surfaces share.
 */
const WHEEL_PX_PER_DOUBLING = 500;
/** A wheel reporting lines (Firefox on some setups) counts a line as this many px. */
const WHEEL_LINE_PX = 20;
/**
 * Pinch-to-zoom arrives as ctrl+wheel with small deltas (a whole pinch is a
 * few dozen px), so it is read this much louder than a scroll.
 */
const WHEEL_PINCH_GAIN = 10;
const WHEEL_MAX_EXPONENT = Math.log2(ZOOM_STEP);

/** The zoom factor one wheel event asks for; 1 when it has no vertical travel. */
function wheelZoomFactor(e: { deltaY: number; deltaMode: number; ctrlKey: boolean }): number {
  const px =
    e.deltaMode === 1
      ? e.deltaY * WHEEL_LINE_PX
      : e.deltaMode === 2
        ? e.deltaY * WHEEL_PX_PER_DOUBLING
        : e.deltaY;
  const exponent = (-px * (e.ctrlKey ? WHEEL_PINCH_GAIN : 1)) / WHEEL_PX_PER_DOUBLING;
  return 2 ** Math.max(-WHEEL_MAX_EXPONENT, Math.min(WHEEL_MAX_EXPONENT, exponent));
}
/** Breathing room between the drawing and the edge of the pane, in px. */
const FIT_PADDING = 24;
/**
 * How far the fit may ENLARGE a small drawing. Filling the pane with a
 * three-node picture is what "fit" means, but past this it stops reading as a
 * diagram and starts reading as a zoomed screenshot.
 */
const MAX_FIT_SCALE = 2;
/**
 * How far it may SHRINK one, which is well past MIN_SCALE on purpose: the
 * reader's own zoom-out stops at a quarter size because nothing below that is
 * worth looking at, but the fit's whole job is showing the drawing entire, and
 * a wide diagram in a pane squeezed to a couple of hundred pixels needs the
 * room. (Stopping at MIN_SCALE was measured clipping a 1127px drawing in a
 * 284px pane.)
 */
const MIN_FIT_SCALE = 0.08;

/**
 * How far a rubber band has to travel before it counts as one. Under this it
 * was a shift-click that landed on empty canvas, and picking every part that
 * happens to sit under a 2px box is not what anyone meant.
 */
const MARQUEE_MIN = 4;

interface Transform {
  scale: number;
  x: number;
  y: number;
}

const IDENTITY: Transform = { scale: 1, x: 0, y: 0 };

/**
 * The gesture in flight. One ref rather than two, so the surface can never
 * believe it is panning and banding at the same time. Marquee coordinates are
 * CLIENT coordinates (what `getBoundingClientRect` speaks, so the hit test
 * needs no arithmetic that has to agree with the transform); `frameLeft/Top`
 * is only there to draw the band inside the pane.
 */
type Gesture =
  | { kind: 'pan'; startX: number; startY: number; originX: number; originY: number; moved: boolean }
  | {
      kind: 'marquee';
      x0: number;
      y0: number;
      x1: number;
      y1: number;
      frameLeft: number;
      frameTop: number;
    };

/** The band as drawn: frame-relative pixels. */
interface Band {
  left: number;
  top: number;
  width: number;
  height: number;
}

function bandOf(g: Extract<Gesture, { kind: 'marquee' }>): Band {
  return {
    left: Math.min(g.x0, g.x1) - g.frameLeft,
    top: Math.min(g.y0, g.y1) - g.frameTop,
    width: Math.abs(g.x1 - g.x0),
    height: Math.abs(g.y1 - g.y0),
  };
}

function clampScale(scale: number): number {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

export function DiagramView({
  resetKey,
  contentKey,
  children,
  onMarqueeSelect,
  onBackgroundClick,
  status,
}: {
  resetKey?: string;
  /**
   * Bumped when the drawing inside lands or is rebuilt (mermaid replaced its
   * placeholder with the real SVG, the hotspots were decorated again). The
   * mount-time fit measured whatever was there first, usually a small
   * placeholder box, which the ResizeObserver below corrects in a visible
   * tab; in a hidden tab (a second window, a switched-away tab) no rendering
   * opportunity fires it, and the reader came back to a drawing framed at
   * 2x with most of it off screen (audit 2026-09-07). This effect is the
   * correction that does not depend on the tab being looked at.
   */
  contentKey?: number | string;
  children: ReactNode;
  /** A shift-drag finished: everything this rectangle touches was picked. */
  onMarqueeSelect?: (rect: ScreenRect) => void;
  /** A press and release on empty canvas, with no pan in between. */
  onBackgroundClick?: () => void;
  /** Quiet corner text (the hint, the count): drawn over, never in the flow. */
  status?: ReactNode;
}) {
  const [t, setT] = useState<Transform>(IDENTITY);
  /** true while the view is the computed fit — nobody has panned or zoomed */
  const [atFit, setAtFit] = useState(true);
  /** whether the last change should animate (a button press, not a drag) */
  const [animate, setAnimate] = useState(false);
  const frameRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const gesture = useRef<Gesture | null>(null);
  /** The band, mirrored into state only so it can be drawn. */
  const [band, setBand] = useState<Band | null>(null);

  /**
   * The transform that centres the whole drawing in the pane. Null when there
   * is nothing to measure yet (the drawing has not landed, the pane is not
   * laid out, or we are in jsdom where every box is 0×0) — the caller falls
   * back to the identity rather than dividing by zero.
   */
  const fitTransform = useCallback((): Transform | null => {
    const frame = frameRef.current;
    const content = contentRef.current;
    if (frame === null || content === null) return null;
    const frameW = frame.clientWidth;
    const frameH = frame.clientHeight;
    // offsetWidth/Height, not getBoundingClientRect: the content is already
    // transformed, and we need its UNSCALED size.
    const contentW = content.offsetWidth;
    const contentH = content.offsetHeight;
    if (frameW <= 0 || frameH <= 0 || contentW <= 0 || contentH <= 0) return null;
    // The margin gives way before the drawing does: in a pane narrowed to a
    // sliver, a fixed 24px each side would be most of the pane.
    const pad = Math.min(FIT_PADDING, frameW / 8, frameH / 8);
    const scale = Math.min(
      MAX_FIT_SCALE,
      Math.max(
        MIN_FIT_SCALE,
        Math.min((frameW - pad * 2) / contentW, (frameH - pad * 2) / contentH),
      ),
    );
    return {
      scale,
      x: (frameW - contentW * scale) / 2,
      y: (frameH - contentH * scale) / 2,
    };
  }, []);

  const fitNow = useCallback(
    (withMotion: boolean) => {
      setAnimate(withMotion);
      setT(fitTransform() ?? IDENTITY);
      setAtFit(true);
    },
    [fitTransform],
  );

  /** A reader-driven change: it holds until they ask for the fit back. */
  const nudge = useCallback((next: Transform | ((prev: Transform) => Transform), withMotion = false) => {
    setAnimate(withMotion);
    setT(next);
    setAtFit(false);
  }, []);

  // A new diagram lands fitted, never inheriting the last one's framing.
  useEffect(() => {
    // The fit is measured from the laid-out DOM, which exists only after the
    // commit: there is no render-time value to derive it from.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fitNow(false);
  }, [resetKey, fitNow]);

  // The drawing inside changed (see contentKey): re-fit unless the reader
  // has framed the view themselves.
  const atFitRef = useRef(atFit);
  useEffect(() => {
    atFitRef.current = atFit;
  }, [atFit]);
  useEffect(() => {
    if (contentKey === undefined) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- measured from the laid-out DOM, as above
    if (atFitRef.current) fitNow(false);
  }, [contentKey, fitNow]);

  // The pane and the drawing both change size without anything re-rendering
  // here: the parts panel docks open under the diagram, the transcript column
  // is dragged, mermaid finishes and replaces a one-line placeholder with a
  // picture. Each of those is a reason to re-fit — unless the reader has
  // framed the view themselves, in which case it is their view to keep.
  useEffect(() => {
    const frame = frameRef.current;
    const content = contentRef.current;
    if (frame === null || content === null) return;
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      if (atFitRef.current) fitNow(false);
    });
    observer.observe(frame);
    observer.observe(content);
    return () => observer.disconnect();
  }, [fitNow]);

  const zoomBy = useCallback(
    (factor: number, origin?: { x: number; y: number }) => {
      nudge((prev) => {
        // Never yank a view UP to the floor: the fit is allowed below it, and
        // zooming out from there should stop, not jump forward.
        const scale = Math.max(Math.min(MIN_SCALE, prev.scale), clampScale(prev.scale * factor));
        if (scale === prev.scale) return prev;
        if (origin === undefined) {
          // No pointer to zoom toward: hold the centre of the pane still.
          const frame = frameRef.current;
          const cx = (frame?.clientWidth ?? 0) / 2;
          const cy = (frame?.clientHeight ?? 0) / 2;
          const ratio = scale / prev.scale;
          return { scale, x: cx - (cx - prev.x) * ratio, y: cy - (cy - prev.y) * ratio };
        }
        // Keep the point under the cursor fixed while the scale changes.
        const ratio = scale / prev.scale;
        return {
          scale,
          x: origin.x - (origin.x - prev.x) * ratio,
          y: origin.y - (origin.y - prev.y) * ratio,
        };
      }, origin === undefined);
    },
    [nudge],
  );

  // Non-passive wheel listener: React's onWheel is passive, so it cannot
  // preventDefault, and the pane would scroll the page instead of zooming.
  useEffect(() => {
    const el = frameRef.current;
    if (el === null) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const factor = wheelZoomFactor(e);
      if (factor === 1) return; // a sideways scroll is not a zoom
      const rect = el.getBoundingClientRect();
      zoomBy(factor, { x: e.clientX - rect.left, y: e.clientY - rect.top });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomBy]);

  return (
    <div
      ref={frameRef}
      data-testid="diagram-view"
      data-scale={t.scale.toFixed(2)}
      className="relative h-full w-full overflow-hidden"
    >
      {/* select-none + touch-none: a drag is a PAN, never a text selection.
          Round 2 item 1 ("when I click to drag, it keeps overlapping with
          highlighting") — mermaid renders real <text> nodes, so without
          this the browser ran its own selection gesture on top of ours. */}
      <div
        data-testid="diagram-surface"
        className="h-full w-full cursor-grab touch-none select-none active:cursor-grabbing"
        onPointerDown={(e) => {
          // Buttons drawn INTO the diagram own their own gesture. That covers
          // the shift-press too: on a part it adds that part to the selection,
          // so no band may start there.
          const target = e.target as Element | null;
          if (typeof target?.closest === 'function' && target.closest(`[${PART_ATTR}]`) !== null) {
            gesture.current = null;
            return;
          }
          if (typeof e.currentTarget.setPointerCapture === 'function') {
            e.currentTarget.setPointerCapture(e.pointerId);
          }
          if ((e.shiftKey || e.metaKey || e.ctrlKey) && onMarqueeSelect !== undefined) {
            const box = frameRef.current?.getBoundingClientRect();
            gesture.current = {
              kind: 'marquee',
              x0: e.clientX,
              y0: e.clientY,
              x1: e.clientX,
              y1: e.clientY,
              frameLeft: box?.left ?? 0,
              frameTop: box?.top ?? 0,
            };
            setBand(bandOf(gesture.current));
            return;
          }
          gesture.current = {
            kind: 'pan',
            startX: e.clientX,
            startY: e.clientY,
            originX: t.x,
            originY: t.y,
            moved: false,
          };
        }}
        onPointerMove={(e) => {
          const g = gesture.current;
          if (g === null) return;
          if (g.kind === 'marquee') {
            g.x1 = e.clientX;
            g.y1 = e.clientY;
            setBand(bandOf(g));
            return;
          }
          // A pointermove with no displacement (browsers fire one under a
          // plain click) is not a pan: it must not mark the view as the
          // reader's, or the next re-fit (the drawing landing after a view
          // switch, the pane resizing) is silently skipped and the reader
          // is left on whatever framing the placeholder got.
          if (e.clientX === g.startX && e.clientY === g.startY && !g.moved) return;
          g.moved = true;
          nudge((prev) => ({
            ...prev,
            x: g.originX + (e.clientX - g.startX),
            y: g.originY + (e.clientY - g.startY),
          }));
        }}
        onPointerUp={() => {
          const g = gesture.current;
          gesture.current = null;
          if (g === null) return;
          if (g.kind === 'marquee') {
            setBand(null);
            const left = Math.min(g.x0, g.x1);
            const right = Math.max(g.x0, g.x1);
            const top = Math.min(g.y0, g.y1);
            const bottom = Math.max(g.y0, g.y1);
            if (right - left < MARQUEE_MIN && bottom - top < MARQUEE_MIN) return;
            onMarqueeSelect?.({ left, top, right, bottom });
            return;
          }
          // A press and release that never moved is a click on the canvas, and
          // a click on the canvas means "nothing, thanks".
          if (!g.moved) onBackgroundClick?.();
        }}
        onPointerCancel={() => {
          gesture.current = null;
          setBand(null);
        }}
      >
        {/* Absolutely placed and shrink-wrapped (w-max): the drawing keeps its
            natural size and the transform above does ALL the framing. A
            flex-centred, full-width box cannot do that job — see the module
            note. */}
        <div
          ref={contentRef}
          data-testid="diagram-transform"
          data-animate={animate ? 'true' : 'false'}
          className="lm-diagram-transform absolute top-0 left-0 w-max"
          style={{
            transform: `translate(${t.x}px, ${t.y}px) scale(${t.scale})`,
            transformOrigin: '0 0',
          }}
        >
          {children}
        </div>
      </div>

      {/* The rubber band lives in the FRAME, not in the transform: it is drawn
          in screen pixels where the reader's pointer is, so it neither scales
          with the drawing nor drifts as the picture pans under it. */}
      {band !== null ? (
        <div
          data-testid="diagram-marquee"
          className="lm-diagram-marquee pointer-events-none absolute z-10"
          style={{ left: band.left, top: band.top, width: band.width, height: band.height }}
        />
      ) : null}

      {/* Bottom-left, mirroring the zoom cluster, and absolutely placed on
          purpose: it appears and disappears with the selection, and nothing in
          the pane may move when it does. */}
      {status !== undefined && status !== null ? (
        <div
          data-testid="diagram-status"
          className="pointer-events-none absolute bottom-3 left-3 z-10 max-w-[60%]"
        >
          {status}
        </div>
      ) : null}

      <div className="absolute right-3 bottom-3 z-10 flex items-center gap-1 rounded-pill border border-default bg-raised p-1 shadow-raised">
        <ZoomButton label="Zoom out" onClick={() => zoomBy(1 / ZOOM_STEP)}>
          −
        </ZoomButton>
        <ZoomButton label="Zoom in" onClick={() => zoomBy(ZOOM_STEP)}>
          +
        </ZoomButton>
        <ZoomButton label="Fit to view" onClick={() => fitNow(true)} disabled={atFit}>
          <FitIcon />
        </ZoomButton>
      </div>
    </div>
  );
}

/** Four corners closing on a frame: fit the whole drawing in the view. */
function FitIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" />
    </svg>
  );
}

function ZoomButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled === true}
      className="flex h-6 min-w-6 cursor-pointer items-center justify-center rounded-pill px-1.5 text-[length:var(--text-ui-size)] text-secondary transition-colors duration-(--motion-hover) hover:bg-inset hover:text-primary disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-secondary"
    >
      {children}
    </button>
  );
}
